import React from 'react';
import PropTypes from 'prop-types';
import { rgba } from 'polished';
import cx from 'classnames';

import { withStyles } from '../tools/withStyles';
import { withSounds } from '../tools/withSounds';
import { Link } from '../components/Link';
import { Main } from '../components/Main';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';
import { IconGithub, IconExternalLink, IconClock, IconExpand } from '../components/Icons';
import projects from '../data/projects';

const styles = theme => ({
  root: {},
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 22,
    marginTop: 24,
    maxWidth: 960,
    '@media (min-width: 680px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 20
    }
  },
  reveal: {
    opacity: 0,
    transform: 'translateY(28px)',
    transition: [
      `opacity ${theme.animation.time * 2}ms ease-out`,
      `transform ${theme.animation.time * 2}ms ease-out`
    ].join(',')
  },
  revealVisible: {
    opacity: 1,
    transform: 'translateY(0)'
  },
  card: {
    display: 'block',
    width: '100%',
    margin: 0,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.22),
    padding: 0,
    color: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'left',
    backgroundColor: rgba(theme.color.background.dark, 0.34),
    cursor: 'pointer',
    overflow: 'hidden',
    appearance: 'none',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.secondary.main, 0.72),
      backgroundColor: rgba(theme.color.secondary.main, 0.05),
      boxShadow: `0 0 18px ${rgba(theme.color.secondary.main, 0.18)}`,
      transform: 'translateY(-3px)'
    },

    '&:hover $image, &:focus $image': {
      transform: 'scale(1.035)'
    }
  },
  visual: {
    position: 'relative',
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.18),
    backgroundColor: '#000',
    overflow: 'hidden',

    '&:before': {
      content: '""',
      display: 'block',
      paddingTop: '54%'
    }
  },
  image: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    transition: `transform ${theme.animation.time}ms ease-out`
  },
  video: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    backgroundColor: '#000'
  },
  body: {
    padding: 20
  },
  title: {
    margin: [0, 0, 12],
    color: theme.color.primary.main,
    fontSize: 22,
    lineHeight: 1.2,
    textShadow: `0 0 6px ${theme.color.secondary.main}`
  },
  soonBadge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    letterSpacing: '0.08em',
    color: theme.color.tertiary.main,
    marginLeft: 8,
    verticalAlign: 'middle',
    padding: [3, 6],
    border: '1px solid ' + rgba(theme.color.tertiary.main, 0.45),
    backgroundColor: rgba(theme.color.tertiary.main, 0.08),
    borderRadius: 3
  },
  message: {
    margin: 0,
    color: theme.color.text.main,
    fontSize: 14,
    lineHeight: 1.55,
    whiteSpace: 'pre-line'
  },
  detail: {
    outline: 'none',
    minHeight: 1,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  detailHeader: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    gap: 12,
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.22)
  },
  detailTopBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    gap: 12
  },
  detailEyebrowRow: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: 10
  },
  backIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    boxSizing: 'border-box',
    margin: 0,
    padding: 0,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    borderRadius: 4,
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    color: theme.color.secondary.main,
    cursor: 'pointer',
    flexShrink: 0,
    transition: `all ${theme.animation.time}ms ease-out`,

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.tertiary.main, 0.8),
      backgroundColor: rgba(theme.color.tertiary.main, 0.2),
      color: theme.color.tertiary.main,
      transform: 'translateX(-2px)'
    }
  },
  backIconSvg: {
    width: 16,
    height: 16,
    fill: 'currentColor',
    display: 'block'
  },
  eyebrow: {
    margin: 0,
    color: theme.color.secondary.main,
    fontSize: 11,
    lineHeight: 1.4,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  },
  detailTitle: {
    margin: 0,
    fontSize: 26,
    color: theme.color.heading.main,
    textShadow: `0 0 10px ${rgba(theme.color.secondary.main, 0.5)}`,
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },
  detailHeaderIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    marginLeft: 'auto'
  },
  headerIconBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    boxSizing: 'border-box',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    borderRadius: 4,
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    color: theme.color.secondary.main,
    textDecoration: 'none',
    flexShrink: 0,
    transition: `all ${theme.animation.time}ms ease-out`,

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.tertiary.main, 0.8),
      backgroundColor: rgba(theme.color.tertiary.main, 0.2),
      color: theme.color.tertiary.main,
      transform: 'translateY(-2px)',
      boxShadow: `0 0 12px ${rgba(theme.color.tertiary.main, 0.35)}`
    }
  },
  headerActionSvg: {
    width: 17,
    height: 17,
    fill: 'currentColor',
    display: 'block'
  },
  detailHeroVisual: {
    position: 'relative',
    width: '100%',
    marginBottom: 28,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.28),
    backgroundColor: '#000',
    overflow: 'hidden',
    boxShadow: `0 0 30px ${rgba(0, 0, 0, 0.9)}`,
    cursor: 'zoom-in',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.secondary.main, 0.72),
      boxShadow: `0 0 24px ${rgba(theme.color.secondary.main, 0.24)}`
    },

    '&:hover $zoomHint, &:focus $zoomHint': {
      opacity: 1,
      borderColor: theme.color.secondary.main,
      color: theme.color.secondary.light
    },

    '& $visual': {
      borderBottom: 'none',
      '&:before': {
        paddingTop: '50%'
      }
    }
  },
  detailContentGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    marginBottom: 32,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  detailMainCol: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  lead: {
    margin: [0, 0, 20],
    color: theme.color.primary.main,
    fontSize: 16,
    lineHeight: 1.65,
    borderLeft: `3px solid ${rgba(theme.color.secondary.main, 0.4)}`,
    paddingLeft: 14,
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },
  overviewSection: {
    marginTop: 20,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  sectionSubTitle: {
    margin: [0, 0, 14],
    fontSize: 17,
    color: theme.color.primary.main,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.06em',
    textTransform: 'uppercase'
  },
  paragraph: {
    margin: [0, 0, 14],
    color: theme.color.text.main,
    fontSize: 14.5,
    lineHeight: 1.75,
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },
  detailSidebar: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box'
  },
  sidebarCard: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.25),
    backgroundColor: rgba(theme.color.background.dark, 0.35),
    padding: [20, 20],
    boxShadow: `0 0 20px ${rgba(0, 0, 0, 0.4)}`,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 24,
    overflow: 'hidden',
    '@media (min-width: 768px)': {
      gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))'
    }
  },
  sidebarHeading: {
    gridColumn: '1 / -1',
    margin: [0, 0, 8],
    paddingBottom: 10,
    borderBottom: '1px solid ' + rgba(theme.color.secondary.dark, 0.18),
    fontSize: 14,
    color: theme.color.heading.main,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.08em',
    textTransform: 'uppercase'
  },
  specBlock: {
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    marginBottom: 0
  },
  specLabel: {
    margin: [0, 0, 8],
    color: theme.color.secondary.main,
    fontSize: 11,
    lineHeight: 1.3,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontFamily: theme.typography.primary
  },
  highlightsList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  highlightItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    margin: [0, 0, 8],
    color: theme.color.text.main,
    fontSize: 13.5,
    lineHeight: 1.5,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    '&:last-child': {
      marginBottom: 0
    }
  },
  highlightBullet: {
    color: theme.color.secondary.main,
    fontSize: 10,
    lineHeight: '20px',
    flexShrink: 0
  },
  highlightText: {
    minWidth: 0,
    flex: 1,
    overflowWrap: 'break-word',
    wordBreak: 'break-word'
  },
  sidebarLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  sidebarLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: [8, 12],
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.32),
    backgroundColor: rgba(theme.color.secondary.main, 0.04),
    color: theme.color.secondary.main,
    fontSize: 12,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    textDecoration: 'none',
    transition: `all ${theme.animation.time}ms ease-out`,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',

    '&:hover, &:focus': {
      borderColor: rgba(theme.color.tertiary.main, 0.65),
      color: theme.color.tertiary.main,
      backgroundColor: rgba(theme.color.tertiary.main, 0.08)
    }
  },
  sidebarLinkDisabled: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: [8, 12],
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.22),
    backgroundColor: 'transparent',
    color: rgba(theme.color.secondary.main, 0.55),
    fontSize: 12,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    opacity: 0.65,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  gallerySection: {
    marginTop: 28,
    paddingTop: 24,
    borderTop: '1px solid ' + rgba(theme.color.secondary.dark, 0.18)
  },
  gallery: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18,
    marginTop: 18,
    '@media (min-width: 820px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
    }
  },
  galleryTitle: {
    margin: [0, 0, 16],
    fontSize: 18,
    color: theme.color.primary.main,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.06em',
    textTransform: 'uppercase'
  },
  galleryFrame: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    backgroundColor: '#000',
    overflow: 'hidden'
  },
  galleryMedia: {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: 780,
    objectFit: 'contain',
    margin: [0, 'auto']
  },
  mediaItem: {
    position: 'relative',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.24),
    backgroundColor: rgba(theme.color.background.dark, 0.28),
    overflow: 'hidden',
    cursor: 'zoom-in',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.secondary.main, 0.72),
      boxShadow: `0 0 24px ${rgba(theme.color.secondary.main, 0.24)}`
    },

    '&:hover $zoomHint, &:focus $zoomHint': {
      opacity: 1,
      borderColor: theme.color.secondary.main,
      color: theme.color.secondary.light
    }
  },
  zoomHint: {
    position: 'absolute',
    top: 10,
    right: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: [4, 8],
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    backgroundColor: rgba(theme.color.background.dark, 0.82),
    color: theme.color.secondary.main,
    fontSize: 10,
    lineHeight: 1.2,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    pointerEvents: 'none',
    opacity: 0.75,
    zIndex: 2,
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    transition: `all ${theme.animation.time}ms ease-out`
  },
  mediaCaption: {
    margin: 0,
    borderTop: '1px solid ' + rgba(theme.color.secondary.dark, 0.18),
    padding: [12, 16],
    color: theme.color.secondary.main,
    fontSize: 12,
    lineHeight: 1.4,
    textTransform: 'uppercase'
  },
  techTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box'
  },
  techTag: {
    padding: [4, 10],
    borderRadius: 8,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.32),
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    color: theme.color.secondary.main,
    fontSize: 11,
    lineHeight: 1.2,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap'
  },
  lightboxOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999,
    backgroundColor: 'rgba(2, 2, 4, 0.94)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box'
  },
  lightboxToolbar: {
    position: 'absolute',
    top: 14,
    left: 14,
    right: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 10,
    padding: [8, 14],
    backgroundColor: rgba(theme.color.background.dark, 0.88),
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.4),
    boxShadow: `0 0 20px ${rgba(0, 0, 0, 0.8)}`
  },
  lightboxInfo: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    overflow: 'hidden'
  },
  lightboxCounter: {
    fontSize: 12,
    color: theme.color.secondary.main,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.08em',
    flexShrink: 0,
    marginRight: 12
  },
  lightboxTitle: {
    margin: 0,
    fontSize: 13,
    color: theme.color.heading.main,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  lightboxCloseBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
    marginLeft: 12,
    padding: 0,
    margin: 0,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.5),
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    color: theme.color.secondary.main,
    fontSize: 16,
    lineHeight: 1,
    cursor: 'pointer',
    transition: `all ${theme.animation.time}ms ease-out`,

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.tertiary.main, 0.8),
      backgroundColor: rgba(theme.color.tertiary.main, 0.2),
      color: theme.color.tertiary.main,
      transform: 'scale(1.08)'
    }
  },
  lightboxStage: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: [68, 54, 20, 54],
    boxSizing: 'border-box',
    '@media (max-width: 600px)': {
      padding: [64, 8, 16, 8]
    }
  },
  lightboxMediaWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: '100%',
    maxHeight: '100%',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    backgroundColor: '#000',
    boxShadow: `0 0 50px rgba(0, 0, 0, 0.95), 0 0 25px ${rgba(theme.color.secondary.main, 0.2)}`,
    overflow: 'hidden'
  },
  lightboxMedia: {
    display: 'block',
    maxWidth: '92vw',
    maxHeight: '80vh',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain'
  },
  lightboxNavBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    padding: 0,
    margin: 0,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.45),
    backgroundColor: rgba(theme.color.background.dark, 0.85),
    color: theme.color.secondary.main,
    fontSize: 26,
    lineHeight: 1,
    cursor: 'pointer',
    zIndex: 15,
    userSelect: 'none',
    transition: `all ${theme.animation.time}ms ease-out`,

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: rgba(theme.color.tertiary.main, 0.8),
      backgroundColor: rgba(theme.color.tertiary.main, 0.2),
      color: theme.color.tertiary.main,
      transform: 'translateY(-50%) scale(1.1)'
    },

    '@media (max-width: 600px)': {
      width: 32,
      height: 32,
      fontSize: 20
    }
  },
  lightboxNavPrev: {
    left: 10
  },
  lightboxNavNext: {
    right: 10
  }
});

const playSound = (sounds, name) => {
  const sound = sounds && sounds[name];

  if (sound) {
    sound.play();
  }
};

const isGif = url => url && url.endsWith('.gif');
const REVEAL_TEXT_DELAY = 140;
const REVEAL_TEXT_DURATION = 420;

class ScrollReveal extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object,
    children: PropTypes.func.isRequired
  };

  state = {
    visible: false,
    textVisible: false
  };

  componentDidMount () {
    if (!window.IntersectionObserver) {
      this.show();
      return;
    }

    this.observer = new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.show();
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.16
    });

    this.observer.observe(this.element);
  }

  componentWillUnmount () {
    clearTimeout(this.textTimer);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  show = () => {
    if (this.state.visible) {
      return;
    }

    playSound(this.props.sounds, 'fade');
    this.setState({ visible: true });
    this.textTimer = setTimeout(() => {
      this.setState({ textVisible: true });
    }, REVEAL_TEXT_DELAY);

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  render () {
    const { classes, children } = this.props;
    const { visible, textVisible } = this.state;

    return (
      <div
        className={`${classes.reveal} ${visible ? classes.revealVisible : ''}`}
        ref={ref => (this.element = ref)}
      >
        {children(textVisible)}
      </div>
    );
  }
}

const TextReveal = ({ children, visible }) => (
  <Text
    stableTime
    typingDuration={REVEAL_TEXT_DURATION}
    animation={{
      independent: true,
      show: visible,
      duration: REVEAL_TEXT_DURATION
    }}
  >
    {children}
  </Text>
);

TextReveal.propTypes = {
  children: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired
};

const MediaVisual = ({ classes, media, title, detail }) => (
  <div className={`${classes.visual} ${detail ? classes.detailVisual : ''}`}>
    {media && media.type === 'video' && isGif(media.src) ? (
      <img
        className={classes.video}
        src={media.src}
        alt={media.title || title}
      />
    ) : media && media.type === 'video' ? (
      <video
        className={classes.video}
        src={media.src}
        poster={media.poster}
        autoPlay
        muted
        loop
        playsInline
        preload='metadata'
      />
    ) : (
      <div
        className={classes.image}
        style={{ backgroundImage: media && media.src && `url(${media.src})` }}
      />
    )}
  </div>
);

MediaVisual.propTypes = {
  classes: PropTypes.object.isRequired,
  media: PropTypes.object,
  title: PropTypes.string.isRequired,
  detail: PropTypes.bool
};

const GalleryVisual = ({ classes, media, title }) => (
  <div className={classes.galleryFrame}>
    {media && media.type === 'video' && isGif(media.src) ? (
      <img
        className={classes.galleryMedia}
        src={media.src}
        alt={media.title || title}
        loading='lazy'
      />
    ) : media && media.type === 'video' ? (
      <video
        className={classes.galleryMedia}
        src={media.src}
        poster={media.poster}
        controls
        autoPlay
        muted
        loop
        playsInline
        preload='metadata'
      />
    ) : (
      <img
        className={classes.galleryMedia}
        src={media && media.src}
        alt={media && (media.title || title)}
        loading='lazy'
      />
    )}
  </div>
);

GalleryVisual.propTypes = {
  classes: PropTypes.object.isRequired,
  media: PropTypes.object,
  title: PropTypes.string.isRequired
};

const getProjectCover = project => ({
  type: 'image',
  src: project.coverImage || project.image,
  title: project.title
});

const getProjectMainMedia = project => {
  if (project.media && project.media.length > 0) {
    return project.media[0];
  }

  if (project.video) {
    return {
      type: 'video',
      src: project.video,
      poster: project.image,
      title: project.title
    };
  }

  return getProjectCover(project);
};

const ProjectVisual = ({ classes, project, detail, cover }) => (
  <MediaVisual
    classes={classes}
    media={cover ? getProjectCover(project) : getProjectMainMedia(project)}
    title={project.title}
    detail={detail}
  />
);

ProjectVisual.propTypes = {
  classes: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  detail: PropTypes.bool,
  cover: PropTypes.bool
};

const ProjectCard = ({ classes, sounds, project, index, visible, onSelect }) => {
  const cardMessage = (project.message || '').replace(/\n?Technologies:.*$/s, '').trim();

  return (
    <button
      type='button'
      className={classes.card}
      onClick={() => onSelect(index)}
      onMouseEnter={() => playSound(sounds, 'hover')}
      aria-label={'View ' + project.title + ' details'}
    >
      <ProjectVisual classes={classes} project={project} cover />
      <div className={classes.body}>
        <h2 className={classes.title}>
          <TextReveal visible={visible}>{project.title}</TextReveal>
          {project.status === 'Soon' && (
            <span className={classes.soonBadge}>(SOON)</span>
          )}
        </h2>
        <p className={classes.message}>
          <TextReveal visible={visible}>{cardMessage}</TextReveal>
        </p>
        {project.technologies && project.technologies.length > 0 && (
          <div className={classes.techTags}>
            {project.technologies.slice(0, 4).map(tech => (
              <span key={tech} className={classes.techTag}>{tech}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
};

ProjectCard.propTypes = {
  classes: PropTypes.object.isRequired,
  sounds: PropTypes.object,
  project: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  visible: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired
};

class ProjectDetail extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    sounds: PropTypes.object,
    project: PropTypes.object.isRequired,
    onBack: PropTypes.func.isRequired,
    innerRef: PropTypes.func
  };

  state = {
    lightboxIndex: null
  };

  componentDidMount () {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeyDown, true);
    }
  }

  componentWillUnmount () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown, true);
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    }
  }

  onKeyDown = event => {
    const { lightboxIndex } = this.state;
    if (lightboxIndex === null) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      this.closeLightbox();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      event.stopPropagation();
      this.prevMedia();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.nextMedia();
    }
  }

  getAllMedia = () => {
    const { project } = this.props;
    if (project.media && project.media.length > 0) {
      return project.media;
    }
    return [getProjectMainMedia(project)];
  }

  openLightbox = index => {
    const { sounds } = this.props;
    playSound(sounds, 'click');
    this.setState({ lightboxIndex: index });
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  closeLightbox = () => {
    const { sounds } = this.props;
    playSound(sounds, 'click');
    this.setState({ lightboxIndex: null });
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  prevMedia = () => {
    const { sounds } = this.props;
    const allMedia = this.getAllMedia();
    if (allMedia.length <= 1) {
      return;
    }
    playSound(sounds, 'click');
    this.setState(prevState => ({
      lightboxIndex: (prevState.lightboxIndex - 1 + allMedia.length) % allMedia.length
    }));
  }

  nextMedia = () => {
    const { sounds } = this.props;
    const allMedia = this.getAllMedia();
    if (allMedia.length <= 1) {
      return;
    }
    playSound(sounds, 'click');
    this.setState(prevState => ({
      lightboxIndex: (prevState.lightboxIndex + 1) % allMedia.length
    }));
  }

  renderLightbox = () => {
    const { classes, sounds, project } = this.props;
    const { lightboxIndex } = this.state;
    const allMedia = this.getAllMedia();

    if (lightboxIndex === null || !allMedia[lightboxIndex]) {
      return null;
    }

    const currentMedia = allMedia[lightboxIndex];
    const isVideo = currentMedia.type === 'video';
    const isGifMedia = isGif(currentMedia.src);

    return (
      <div
        className={classes.lightboxOverlay}
        onClick={this.closeLightbox}
        role='dialog'
        aria-modal='true'
        aria-label={currentMedia.title || project.title}
      >
        <div
          className={classes.lightboxToolbar}
          onClick={e => e.stopPropagation()}
        >
          <div className={classes.lightboxInfo}>
            <span className={classes.lightboxCounter}>
              {`[ ${lightboxIndex + 1} / ${allMedia.length} ]`}
            </span>
            <h3 className={classes.lightboxTitle}>
              {currentMedia.title || project.title}
            </h3>
          </div>
          <button
            type='button'
            className={classes.lightboxCloseBtn}
            onClick={this.closeLightbox}
            onMouseEnter={() => playSound(sounds, 'hover')}
            aria-label='Close preview'
          >
            ✕
          </button>
        </div>

        <div
          className={classes.lightboxStage}
          onClick={this.closeLightbox}
        >
          {allMedia.length > 1 && (
            <button
              type='button'
              className={cx(classes.lightboxNavBtn, classes.lightboxNavPrev)}
              onClick={e => {
                e.stopPropagation();
                this.prevMedia();
              }}
              onMouseEnter={() => playSound(sounds, 'hover')}
              aria-label='Previous media'
            >
              ‹
            </button>
          )}

          <div
            className={classes.lightboxMediaWrap}
            onClick={e => e.stopPropagation()}
          >
            {isVideo && !isGifMedia ? (
              <video
                className={classes.lightboxMedia}
                src={currentMedia.src}
                poster={currentMedia.poster}
                controls
                autoPlay
                playsInline
              />
            ) : (
              <img
                className={classes.lightboxMedia}
                src={currentMedia.src}
                alt={currentMedia.title || project.title}
              />
            )}
          </div>

          {allMedia.length > 1 && (
            <button
              type='button'
              className={cx(classes.lightboxNavBtn, classes.lightboxNavNext)}
              onClick={e => {
                e.stopPropagation();
                this.nextMedia();
              }}
              onMouseEnter={() => playSound(sounds, 'hover')}
              aria-label='Next media'
            >
              ›
            </button>
          )}
        </div>
      </div>
    );
  }

  render () {
    const { classes, sounds, project, onBack, innerRef } = this.props;
    const technologies = project.technologies || [];
    const details = project.details || [];
    const highlights = project.highlights || [];
    const media = project.media || [getProjectMainMedia(project)];
    const cleanHighlights = highlights.filter(hl => {
      const lower = hl.toLowerCase().trim();
      return !technologies.some(tech => lower === tech.toLowerCase().trim() || lower.includes(tech.toLowerCase().trim() + ' stack'));
    });

    return (
      <article className={classes.detail} ref={innerRef} tabIndex='-1'>
        <Secuence stagger>
          <header className={classes.detailHeader}>
            <div className={classes.detailTopBar}>
              <div className={classes.detailEyebrowRow}>
                <button
                  type='button'
                  className={classes.backIconBtn}
                  onClick={onBack}
                  aria-label='Back to projects'
                  title='Back to Projects'
                  onMouseEnter={() => playSound(sounds, 'hover')}
                >
                  <svg viewBox='0 0 24 24' className={classes.backIconSvg}>
                    <path fill='currentColor' d='M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z' />
                  </svg>
                </button>
                <p className={classes.eyebrow}><Text>Project Detail</Text></p>
              </div>
              <div className={classes.detailHeaderIcons}>
                {!(project.status === 'Soon' || project.soon) && project.link && (
                  <Link
                    className={classes.headerIconBtn}
                    href={project.link}
                    target={project.slug + '-project'}
                    rel='noreferrer'
                    aria-label='Website'
                    title='Website'
                    onMouseEnter={() => playSound(sounds, 'hover')}
                  >
                    <svg viewBox='0 0 24 24' className={classes.headerActionSvg}>
                      <path fill='currentColor' d='M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7z' />
                    </svg>
                  </Link>
                )}
                {project.github && (
                  <Link
                    className={classes.headerIconBtn}
                    href={project.github}
                    target={project.slug + '-github'}
                    rel='noreferrer'
                    aria-label='GitHub'
                    title='GitHub'
                    onMouseEnter={() => playSound(sounds, 'hover')}
                  >
                    <svg viewBox='0 0 24 24' className={classes.headerActionSvg}>
                      <path fill='currentColor' d='M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.1-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z' />
                    </svg>
                  </Link>
                )}
              </div>
            </div>
            <h1 className={classes.detailTitle}>
              <Text>{project.title}</Text>
              {project.status === 'Soon' && (
                <span className={classes.soonBadge}>(SOON)</span>
              )}
            </h1>
          </header>

          <div
            className={classes.detailHeroVisual}
            onClick={() => this.openLightbox(0)}
            onMouseEnter={() => playSound(sounds, 'hover')}
            role='button'
            tabIndex='0'
            aria-label='Enlarge project media'
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.openLightbox(0);
              }
            }}
          >
            <ProjectVisual classes={classes} project={project} detail />
            <span className={classes.zoomHint}>
              <IconExpand size='1em' />
              <span>Click to Enlarge</span>
            </span>
          </div>

          <div className={classes.detailContentGrid}>
            <div className={classes.detailMainCol}>
              <p className={classes.lead}>{project.summary}</p>

              {details.length > 0 && (
                <div className={classes.overviewSection}>
                  <h2 className={classes.sectionSubTitle}><Text>Overview & Architecture</Text></h2>
                  {details.map((paragraph, index) => (
                    <p className={classes.paragraph} key={index}>{paragraph}</p>
                  ))}
                </div>
              )}
            </div>

            <aside className={classes.detailSidebar}>
              <div className={classes.sidebarCard}>
                <h3 className={classes.sidebarHeading}><Text>Project Specs</Text></h3>

                {technologies.length > 0 && (
                  <div className={classes.specBlock}>
                    <h4 className={classes.specLabel}>Technology Stack</h4>
                    <div className={classes.techTags}>
                      {technologies.map(tech => (
                        <span key={tech} className={classes.techTag}>{tech}</span>
                      ))}
                    </div>
                  </div>
                )}

                {cleanHighlights.length > 0 && (
                  <div className={classes.specBlock}>
                    <h4 className={classes.specLabel}>Key Capabilities</h4>
                    <ul className={classes.highlightsList}>
                      {cleanHighlights.map(highlight => (
                        <li key={highlight} className={classes.highlightItem}>
                          <span className={classes.highlightBullet}>▸</span>
                          <span className={classes.highlightText}>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(project.link || project.github || project.status === 'Soon' || project.soon) && (
                  <div className={classes.specBlock}>
                    <h4 className={classes.specLabel}>Resources</h4>
                    <div className={classes.sidebarLinks}>
                      {project.status === 'Soon' || project.soon ? (
                        <span className={cx(classes.sidebarLink, classes.sidebarLinkDisabled)}>
                          <span>Live Platform (SOON)</span>
                          <IconClock size='1em' />
                        </span>
                      ) : (
                        project.link && (
                          <Link
                            className={classes.sidebarLink}
                            href={project.link}
                            target={project.slug + '-project'}
                            rel='noreferrer'
                            onMouseEnter={() => playSound(sounds, 'hover')}
                          >
                            <span>Live Platform</span>
                            <IconExternalLink size='1em' />
                          </Link>
                        )
                      )}
                      {project.github && (
                        <Link
                          className={classes.sidebarLink}
                          href={project.github}
                          target={project.slug + '-github'}
                          rel='noreferrer'
                          onMouseEnter={() => playSound(sounds, 'hover')}
                        >
                          <span>Source Repository</span>
                          <IconGithub size='1em' />
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>

          {media.length > 1 && (
            <section className={classes.gallerySection}>
              <h2 className={classes.galleryTitle}><Text>Additional Media</Text></h2>
              <div className={classes.gallery}>
                {media.slice(1).map((item, index) => (
                  <ScrollReveal
                    key={item.src + index}
                    classes={classes}
                    sounds={sounds}
                  >
                    {visible => (
                      <div
                        className={classes.mediaItem}
                        onClick={() => this.openLightbox(index + 1)}
                        onMouseEnter={() => playSound(sounds, 'hover')}
                        role='button'
                        tabIndex='0'
                        aria-label={'Enlarge ' + (item.title || project.title)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            this.openLightbox(index + 1);
                          }
                        }}
                      >
                        <GalleryVisual
                          classes={classes}
                          media={item}
                          title={project.title}
                        />
                        <span className={classes.zoomHint}>
                          <IconExpand size='1em' />
                          <span>Click to Enlarge</span>
                        </span>
                        <p className={classes.mediaCaption}>
                          <TextReveal visible={visible}>
                            {item.title || project.title}
                          </TextReveal>
                        </p>
                      </div>
                    )}
                  </ScrollReveal>
                ))}
              </div>
            </section>
          )}
        </Secuence>
        {this.renderLightbox()}
      </article>
    );
  }
}

class Projelerim extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
    sounds: PropTypes.object
  };

  state = {
    selectedProjectIndex: null
  };

  componentDidMount () {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      const index = projects.findIndex(p => p.slug === hash);

      if (index !== -1) {
        this.setState({ selectedProjectIndex: index });
      }

      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('route-change', this.onURLChange);
      window.addEventListener('keydown', this.onKeyDown);
    }
  }

  componentWillUnmount () {
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('route-change', this.onURLChange);
      window.removeEventListener('keydown', this.onKeyDown);
    }
  }

  onPopState = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const hash = window.location.hash.replace('#', '');
    const index = projects.findIndex(p => p.slug === hash);
    this.setState({ selectedProjectIndex: index !== -1 ? index : null });
  }

  onURLChange = () => {
    if (typeof window !== 'undefined' && !window.location.hash) {
      this.setState({ selectedProjectIndex: null });
    }
  }

  onKeyDown = (event) => {
    if (event.key === 'Escape' && this.state.selectedProjectIndex !== null) {
      this.onProjectsBack();
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { selectedProjectIndex } = this.state;

    if (
      selectedProjectIndex !== null &&
      prevState.selectedProjectIndex !== selectedProjectIndex
    ) {
      this.scrollToTop();
    }
  }

  scrollToTop = () => {
    let node = this.detailElement;
    while (node) {
      if (node.scrollTop > 0) {
        node.scrollTop = 0;
      }
      node = node.parentElement;
    }

    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }

  onProjectSelect = index => {
    const { sounds } = this.props;
    const project = projects[index];

    playSound(sounds, 'click');
    this.setState({ selectedProjectIndex: index });

    if (typeof window !== 'undefined' && project) {
      window.history.pushState({ projectIndex: index, projectSlug: project.slug }, '', '#' + project.slug);
    }
  }

  onProjectsBack = () => {
    const { sounds } = this.props;

    playSound(sounds, 'click');
    this.setState({ selectedProjectIndex: null });

    if (typeof window !== 'undefined' && window.location.hash) {
      window.history.pushState(null, '', window.location.pathname);
    }
  }

  render () {
    const { classes, sounds } = this.props;
    const { selectedProjectIndex } = this.state;
    const selectedProject = projects[selectedProjectIndex];

    return (
      <Main className={classes.root}>
        {selectedProject ? (
          <ProjectDetail
            classes={classes}
            sounds={sounds}
            project={selectedProject}
            onBack={this.onProjectsBack}
            innerRef={ref => (this.detailElement = ref)}
          />
        ) : (
          <Secuence stagger>
            <h1><Text>Projects</Text></h1>
            <div className={classes.grid}>
              {projects.map((project, index) => (
                <ScrollReveal
                  key={project.title}
                  classes={classes}
                  sounds={sounds}
                >
                  {visible => (
                    <ProjectCard
                      classes={classes}
                      sounds={sounds}
                      project={project}
                      index={index}
                      visible={visible}
                      onSelect={this.onProjectSelect}
                    />
                  )}
                </ScrollReveal>
              ))}
            </div>
          </Secuence>
        )}
      </Main>
    );
  }
}

export default withStyles(styles)(withSounds()(Projelerim));

// Commit trace: 2025-07-17T10:18:47 - refactor: clean up code structure in projelerim.js

// Commit trace: 2025-08-11T14:16:03 - feat: enhance functionality of projelerim.js

// Commit trace: 2025-10-04T23:57:32 - docs: clarify parameter types in projelerim.js

// Commit trace: 2025-12-30T12:54:21 - perf: improve memory usage in projelerim.js
